import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CollapsibleChartGroup from './CollapsibleChartGroup';

const DailyTrendChart = ({ data, loading, chartType, trendType }) => {
    const theme = useTheme();

    if (loading) {

        return (
            <Box
                className="glass-panel flex-center"
                sx={{
                    height: '100%',
                    minHeight: '400px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                <Typography color="text.secondary">Loading chart data...</Typography>
            </Box>
        );
    }

    const rawData = data || [];

    // 2m Depth Parameters
    const params2m = [
        { key: 'at2mDegC', label: 'Temperature (°C)', color: '#c30a0aff', strokeWidth: 3, defaultOn: true, aggregation: 'AVG' },
        { key: 'rh2mPer', label: 'Humidity (%)', color: '#1565C0', strokeWidth: 3, defaultOn: true, aggregation: 'AVG' },
        { key: 'rainFallMm', label: 'Rainfall (mm)', color: '#311B92', strokeWidth: 3, defaultOn: true, aggregation: 'SUM' },
        { key: 'avgNetRadiationwBym2', label: 'Net Rad (W/m²)', color: '#eee30dff', strokeWidth: 3, defaultOn: true, aggregation: 'AVG' },
        { key: 'ws2mMps', label: 'Wind Spd (m/s)', color: '#2E7D32', strokeWidth: 3, defaultOn: true, aggregation: 'AVG' },
        { key: 'wd2mDeg', label: 'Wind Dir (°)', color: '#00897B', strokeWidth: 3, defaultOn: true, aggregation: 'AVG' },
        { key: 'ap2mHpa', label: 'Pressure (hPa)', color: '#7B1FA2', strokeWidth: 3, defaultOn: true, aggregation: 'AVG' }
    ];

    // 4m Depth Parameters
    const params4m = [
        { key: 'at4mDegC', label: 'Temperature (°C)', color: '#c30a0aff', strokeWidth: 3, defaultOn: true, aggregation: 'AVG' },
        { key: 'rh4mPer', label: 'Humidity (%)', color: '#1565C0', strokeWidth: 3, defaultOn: true, aggregation: 'AVG' },
        { key: 'rainFallMm', label: 'Rainfall (mm)', color: '#311B92', strokeWidth: 3, defaultOn: true, aggregation: 'SUM' },
        { key: 'avgNetRadiationwBym2', label: 'Net Rad (W/m²)', color: '#eee30dff', strokeWidth: 3, defaultOn: true, aggregation: 'AVG' },
        { key: 'ws4mMps', label: 'Wind Spd (m/s)', color: '#2E7D32', strokeWidth: 3, defaultOn: true, aggregation: 'AVG' },
        { key: 'wd4mDeg', label: 'Wind Dir (°)', color: '#00897B', strokeWidth: 3, defaultOn: true, aggregation: 'AVG' }, // base teal

    ];

    // 6m Depth Parameters
    const params6m = [
        { key: 'at6mDegC', label: 'Temperature (°C)', color: '#c30a0aff', strokeWidth: 3, defaultOn: true, aggregation: 'AVG' },
        { key: 'rh6mPer', label: 'Humidity (%)', color: '#1565C0', strokeWidth: 3, defaultOn: true, aggregation: 'AVG' },
        { key: 'rainFallMm', label: 'Rainfall (mm)', color: '#311B92', strokeWidth: 3, defaultOn: true, aggregation: 'SUM' },
        { key: 'avgNetRadiationwBym2', label: 'Net Rad (W/m²)', color: '#eee30dff', strokeWidth: 3, defaultOn: true, aggregation: 'AVG' },
        { key: 'ws6mMps', label: 'Wind Spd (m/s)', color: '#2E7D32', strokeWidth: 3, defaultOn: true, aggregation: 'AVG' },
        { key: 'wd6mDeg', label: 'Wind Dir (°)', color: '#00897B', strokeWidth: 3, defaultOn: true, aggregation: 'AVG' },

    ];

    // Combined Depth Parameters
    const paramsCombined = [
        // temperature
        { key: 'at2mDegC', label: 'Temp (2m) °C', color: '#e53935', strokeWidth: 3, defaultOn: true, aggregation: 'AVG' },
        { key: 'at4mDegC', label: 'Temp (4m) °C', color: '#c30a0aff', strokeWidth: 3, defaultOn: true, aggregation: 'AVG' },
        { key: 'at6mDegC', label: 'Temp (6m) °C', color: '#7f0000', strokeWidth: 3, defaultOn: true, aggregation: 'AVG' },
        //Humidity
        { key: 'rh2mPer', label: 'Hum (2m) %', color: '#1E88E5', strokeWidth: 3, defaultOn: true, aggregation: 'AVG' },
        { key: 'rh4mPer', label: 'Hum (4m) %', color: '#1565C0', strokeWidth: 3, defaultOn: true, aggregation: 'AVG' },
        { key: 'rh6mPer', label: 'Hum (6m) %', color: '#0D47A1', strokeWidth: 3, defaultOn: true, aggregation: 'AVG' },
        { key: 'rainFallMm', label: 'Rain (2m) mm', color: '#311B92', strokeWidth: 3, defaultOn: true, aggregation: 'SUM' },
        { key: 'avgNetRadiationwBym2', label: 'Net Rad (2m)', color: '#eee30dff', strokeWidth: 3, defaultOn: true, aggregation: 'AVG' },
        //wind speed
        { key: 'ws2mMps', label: 'Wind Spd (2m)', color: '#43A047', strokeWidth: 3, defaultOn: true, aggregation: 'AVG' },
        { key: 'ws4mMps', label: 'Wind Spd (4m)', color: '#2E7D32', strokeWidth: 3, defaultOn: true, aggregation: 'AVG' },
        { key: 'ws6mMps', label: 'Wind Spd (6m)', color: '#106314ff', strokeWidth: 3, defaultOn: true, aggregation: 'AVG' },
        // wind direction
        { key: 'wd2mDeg', label: 'Wind Dir (2m)', color: '#4DB6AC', strokeWidth: 3, defaultOn: true, aggregation: 'AVG' }, // light teal
        { key: 'wd4mDeg', label: 'Wind Dir (4m)', color: '#00897B', strokeWidth: 3, defaultOn: true, aggregation: 'AVG' }, // base teal
        { key: 'wd6mDeg', label: 'Wind Dir (6m)', color: '#004D40', strokeWidth: 3, defaultOn: true, aggregation: 'AVG' }, // dark teal
        { key: 'ap2mHpa', label: 'Pressure (2m)', color: '#7B1FA2', strokeWidth: 3, defaultOn: true, aggregation: 'AVG' },

    ];

    return (
        <Box sx={{ padding: theme.spacing(2) }}>
            <Typography
                variant="h4"
                sx={{
                    fontWeight: 700,
                    color: theme.palette.primary.main,
                    marginBottom: theme.spacing(3),
                    textAlign: 'center'
                }}
            >
                Daily Weather Trends
            </Typography>

            {rawData.length > 0 ? (
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'minmax(0, 1fr)',
                        gap: theme.spacing(2)
                    }}
                >
                    {/* 2m Depth Group */}
                    <CollapsibleChartGroup
                        title="Weather Trends – 2m Depth"
                        icon="📈"
                        parameters={params2m}
                        data={rawData}
                        defaultExpanded={true}
                        gradientColors={[theme.palette.primary.main, theme.palette.primary.light]}
                        topBorderColor={theme.palette.primary.main}
                        chartType={chartType}
                        trendType={trendType}
                    />

                    {/* 4m Depth Group */}
                    <CollapsibleChartGroup
                        title="Weather Trends – 4m Depth"
                        icon="📈"
                        parameters={params4m}
                        data={rawData}
                        defaultExpanded={true}
                        gradientColors={[theme.palette.secondary.main, theme.palette.secondary.light]}
                        topBorderColor={theme.palette.secondary.main}
                        chartType={chartType}
                        trendType={trendType}
                    />

                    {/* 6m Depth Group */}
                    <CollapsibleChartGroup
                        title="Weather Trends – 6m Depth"
                        icon="📈"
                        parameters={params6m}
                        data={rawData}
                        defaultExpanded={true}
                        gradientColors={['#01383eff', '#015c66']}
                        topBorderColor="#01383eff"
                        chartType={chartType}
                        trendType={trendType}
                    />

                    {/* Combined Depth Group */}
                    <CollapsibleChartGroup
                        title="Combined Depth Trends (2m, 4m, 6m)"
                        icon="📊"
                        parameters={paramsCombined}
                        data={rawData}
                        defaultExpanded={true}
                        gradientColors={[theme.palette.info.main, theme.palette.info.light]}
                        topBorderColor={theme.palette.info.main}
                        chartType={chartType}
                        trendType={trendType}
                    />
                </Box>
            ) : (
                <Box
                    sx={{
                        textAlign: 'center',
                        padding: theme.spacing(6),
                        color: theme.palette.text.secondary,
                        fontSize: '1rem'
                    }}
                >
                    No data loaded. Select filters and click View Trend.
                </Box>
            )}
        </Box>
    );
};

export default DailyTrendChart;
